/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { AudioChunkAccumulator } from "./AudioChunkAccumulator";
import { useSocketIoClient } from "./useSocketIoClient";
import { useTranscriptionStreamingStore } from "@/store/transcriptionStreaming.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useAuthStore } from "@/store/auth.store";
import { TranscriberClientEvents, TranscriberServerEvents } from "./constants";
import type { IUseAudioTranscription } from "./useAudioTranscriptionFactory";
import type { TranscriptCompletedMessage, TranscriptEndMessage } from "./types";

export const useAudioTranscription = (): IUseAudioTranscription => {
  const { socket } = useSocketIoClient();
  const [transcription, setTranscription] = useState<string>("");
  const [finished, setFinished] = useState(false);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const accumulatorRef = useRef<AudioChunkAccumulator>(
    new AudioChunkAccumulator(80000)
  );
  const { setStreamText, resetStreamText, finishSteamText } =
    useTranscriptionStreamingStore.getState();
  const {
    inputLanguage,
    noiseReduction,
    transcriptionProvider,
    noiseSuppression,
    diarize,
  } = useRecordingSettingsStore.getState();
  const isAudioTranscriptionPaused = useRef(true);
  const { userData } = useAuthStore.getState();
  const [isProviderReady, setIsProviderReady] = useState(false);

  const submitChunk = (isLast: boolean = false): void => {
    const accumulator = accumulatorRef.current;
    const pcm16 = accumulator.merged;
    socket.emit(TranscriberClientEvents.AUDIO_CHUNK, {
      body: pcm16,
      isLast: isLast,
    });
    accumulator.reset();
  };

  const resetStreams = (): void => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    audioStreamRef.current = null;
  };

  const reset = (): void => {
    resetStreams();
    accumulatorRef.current.reset();
    setFinished(false);
    resetStreamText();
  };

  const finishSession = (): void => {
    submitChunk(true);
    resetStreams();
    resetStreamText();
  };

  const startSession = (): void => {
    socket.emit(TranscriberClientEvents.AUDIO_START, {
      inputLanguage: inputLanguage,
      noiseReduction: noiseReduction,
      userEmail: userData?.email,
      userId: userData?.id,
      transcriptionProvider: transcriptionProvider,
      noiseSuppression: noiseSuppression,
      diarize: diarize,
    });
  };

  const pauseStream = (): void => {
    isAudioTranscriptionPaused.current = true;
  };

  const setStream = (stream: MediaStream): void => {
    isAudioTranscriptionPaused.current = false;
    if (audioStreamRef.current) {
      return;
    }
    reset();
    audioStreamRef.current = stream;
    const accumulator = accumulatorRef.current;
    processorRef.current =
      AudioChunkAccumulator.fromMediaStreamToAudioScriptProcessor(stream);
    processorRef.current.onaudioprocess = (event): void => {
      if (isAudioTranscriptionPaused.current) {
        return;
      }
      accumulator.addChunk(event);
      if (accumulator.isReadyToSend) {
        console.log("submitting chunk");
        submitChunk();
      }
    };
  };

  useEffect(() => {
    socket.once("connect", startSession);
  }, []);

  const handlers = useMemo(() => {
    return {
      [TranscriberServerEvents.CONNECT_ERROR]: (): void => {
        // TODO: show error when the connection fails
      },
      [TranscriberServerEvents.PROVIDER_READY]: (): void => {
        setIsProviderReady(true);
      },
      [TranscriberServerEvents.PROVIDER_WAIT]: (): void => {
        setIsProviderReady(false);
      },
      [TranscriberServerEvents.TRANSCRIPT_PARTIAL]: (): void => {},
      [TranscriberServerEvents.TRANSCRIPT_COMPLETED]: (
        data: TranscriptCompletedMessage
      ): void => {
        setStreamText({
          text: data.transcription,
          startTime: data.startTime,
        });
      },
      [TranscriberServerEvents.TRANSCRIPT_END]: (
        data: TranscriptEndMessage
      ): void => {
        finishSteamText();
        setTranscription(data.transcription);
        setFinished(true);
      },
    };
  }, [finishSteamText, setFinished, setStreamText]);

  useEffect(() => {
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });
    return (): void => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, handlers]);

  return {
    audioStream: audioStreamRef.current,
    setStream,
    transcription: transcription,
    finishSession,
    finished,
    reset,
    pauseStream,
    isProviderReady,
  };
};
